import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV = [
  {
    label: "Vision",
    to: "/vision",
    children: [
      { label: "Our Vision", to: "/vision" },
      { label: "About William Martin", to: "/about" },
    ],
  },
  { label: "What We Build", to: "/what-we-build" },
  { label: "AI on Country", to: "/ai-on-country" },
  { label: "Stewardship Intelligence", to: "/stewardship-intelligence" },
  {
    label: "Initiatives",
    to: "/initiatives",
    children: [
      { label: "Featured Initiatives", to: "/initiatives" },
      { label: "Participatory Video", to: "/participatory-video" },
    ],
  },
  { label: "Living Network", to: "/network" },
  { label: "Partner", to: "/partner" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-700 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050806]/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo-badge-white.png"
            alt=""
            className="h-[2.6rem] w-[2.6rem] object-contain group-hover:scale-105 transition-transform duration-500"
          />
          <img
            src="/wordmark-cream.png"
            alt="WisdomKeepers"
            className="h-11 w-auto object-contain"
          />
        </Link>

        <div className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.to} className="relative group">
                <Link
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-1 text-[13px] tracking-wide whitespace-nowrap transition-colors duration-300 ${
                    item.children.some((c) => c.to === pathname) || pathname === item.to
                      ? "text-[#D4A373]"
                      : "text-[#A8A5A1] hover:text-[#F4F1ED]"
                  }`}
                >
                  {item.label}
                  <ChevronDown size={13} className="mt-px transition-transform duration-300 group-hover:rotate-180" />
                </Link>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 hidden group-hover:block">
                  <div className="min-w-[230px] rounded-lg border border-white/10 bg-[#0A0F0C]/95 backdrop-blur-xl p-2 shadow-2xl">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`block px-4 py-3 rounded-md text-[13px] tracking-wide transition-colors duration-300 ${
                          pathname === c.to ? "text-[#D4A373] bg-white/5" : "text-[#A8A5A1] hover:text-[#F4F1ED] hover:bg-white/5"
                        }`}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[13px] tracking-wide whitespace-nowrap transition-colors duration-300 ${
                  pathname === item.to
                    ? "text-[#D4A373]"
                    : "text-[#A8A5A1] hover:text-[#F4F1ED]"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
          <Link
            to="/partner"
            data-testid="nav-cta-button"
            className="text-[13px] tracking-wide px-4 py-2.5 rounded-md border border-[#D4A373]/60 text-[#F4F1ED] hover:bg-[#D4A373] hover:text-[#050806] transition-all duration-500 whitespace-nowrap"
          >
            Start a Conversation
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden text-[#F4F1ED]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden backdrop-blur-xl bg-[#050806]/95 border-t border-white/10 px-6 py-8 flex flex-col gap-6"
        >
          {NAV.map((item) => (
            <div key={item.to} className="flex flex-col gap-3">
              <Link
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-lg font-display text-[#F4F1ED]"
              >
                {item.label}
              </Link>
              {item.children && (
                <div className="flex flex-col gap-3 pl-4 border-l border-white/10">
                  {item.children.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      data-testid={`mobile-nav-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-base text-[#A8A5A1]"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
}
